import { belongsTo, Factory, Model } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import type { MirageServer, MockServer } from '@/api/mock-server/server'
import { randPastDate, randUuid, randParagraph } from '@ngneat/falso'
import type { SchemaAttributes } from '@/api/types'
import { generateFinanceAmount } from '@/api/mock-server/utils.js'
import { roundEven, toNumber } from '@/utils/number.js'

type UnallocatedPaymentData = SchemaAttributes<'UnallocatedPayment'>

export const model = Model.extend({
  client: belongsTo('Client'),
  department: belongsTo('Department'),
  paymentMethod: belongsTo('PaymentMethod'),
}) as ModelDefinition<UnallocatedPaymentData>

export const factory = Factory.extend<UnallocatedPaymentData>({
  payment_type() {
    return 'PAYMENT_PRESENT_CARD'
  },
  paid() {
    return roundEven(toNumber(generateFinanceAmount({ min: 50, max: 5000 })), 2)
  },
  unused_amount() {
    // Start as fully unused to match the Use Prepayment flow
    return toNumber(this.paid)
  },
  fully_used() {
    return false
  },
  description() {
    const text = randParagraph()
    const raw = Array.isArray(text) ? text[0] : text
    return String(raw).slice(0, 40)
  },
  date_added() {
    return randPastDate().toISOString()
  },
  valid_until() {
    return null
  },
})

export function routes(server: MockServer) {
  server.get(
    '/unallocated_payments/:id',
    function (this: MirageServer, schema, request) {
      const { id } = request.params

      // Ensure essential related data
      const department =
        schema.first('department') || server.create('department')

      let client = schema.first('client')
      if (!client) {
        client = server.create('client', { id: randUuid() })
      }

      let paymentMethod = schema.first('paymentMethod')
      if (!paymentMethod) {
        paymentMethod = server.create('paymentMethod')
      }

      let unallocated = schema.find('unallocatedPayment', id)
      if (!unallocated) {
        unallocated = server.create('unallocatedPayment', {
          id,
          client,
          department,
          paymentMethod,
        })
      }

      return unallocated
    },
  )

  server.post(
    '/unallocated_payments/:id/use',
    function (this: MirageServer, schema, request) {
      const { id } = request.params

      let unallocated = schema.find('unallocatedPayment', id)
      if (!unallocated) {
        unallocated = server.create('unallocatedPayment', { id })
      }

      const parsed = JSON.parse(request.requestBody || '{}') as {
        data?: {
          attributes?: {
            invoices?: Array<{
              data?: { invoice_id?: number; amount?: number }
            }>
          }
        }
      }

      const invoices = parsed?.data?.attributes?.invoices || []
      const totalApplied = invoices.reduce((sum, item) => {
        const amount = Number(item?.data?.amount ?? 0)
        return sum + (isFinite(amount) ? amount : 0)
      }, 0)

      const currentUnused = Number(unallocated.attrs.unused_amount || 0)
      const newUnused = Math.max(0, roundEven(currentUnused - totalApplied, 2))

      unallocated.update({
        unused_amount: newUnused,
        fully_used: newUnused === 0,
      })

      return {
        data: {
          type: 'UsePrepaymentResult',
          attributes: {
            payments: [],
          },
        },
      }
    },
  )
}
